import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createErrorComponent } from "components/Errors/ErrorComponent";
import { DataDisplay } from "components/IDataDisplayer/DataDisplayType";
import PlanDisplayer from "components/IDataDisplayer/WarehouseGridAdapter/PlanDisplayer";
import { useStatusService } from "hooks/useStatusService";
import { useWarehousePlan } from "hooks/useWarehousePlan";
import { JSX, useMemo, useEffect } from "react";
import { getWarehousePlan } from "store/Selectors/warehousePlanSelector";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import {
  addLoadingRequest,
  removeLoadingRequest,
} from "store/Slices/appStateSlice";
import WarehouseSearchZone from "components/WarehouseElements/WarehouseHeaders/WarehouseSearchZone/WarehouseSearchZone";
import { useOutletContext } from "react-router";
import { VIEW_MODE } from "constants/navigation";
import { OutletContext } from "pages/Home/Home";
import { setLegendDisplay } from "store/Slices/warehousePlanSlice";

import "./PlanPage.scss";
import { selectIsAppLoading } from "store/Selectors/appStateSelector";

type Props = {
  displayType?: DataDisplay;
};
export function PlanPage({
  displayType = DataDisplay.GRID,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    storeNb: currentLocationStoreNb,
    viewMode,
    legendDisplay,
  }: OutletContext = useOutletContext();

  const { error: statusError, isLoading: isStatusLoading } = useStatusService(
    currentLocationStoreNb,
    0
  );

  dispatch(setLegendDisplay(legendDisplay));
  const { isLoading: isWarehousePlanLoading, error: planError } =
    useWarehousePlan(currentLocationStoreNb);
  const isDisplayerReady = !useAppSelector(selectIsAppLoading);
  const activeWarehouse = useAppSelector(getWarehousePlan);

  useEffect(() => {
    if (isWarehousePlanLoading || isStatusLoading) {
      dispatch(addLoadingRequest("planPage"));
    } else {
      dispatch(removeLoadingRequest("planPage"));
    }
    return () => {
      dispatch(removeLoadingRequest("planPage"));
    };
  }, [isWarehousePlanLoading, isStatusLoading, dispatch]);

  const isFetchedDataError = useMemo(
    () => statusError || planError,
    [statusError, planError]
  );
  if (!isDisplayerReady) {
    return <h1 className="loading-text">Chargement du plan...</h1>;
  } else if (isFetchedDataError) {
    return createDisplayerErrorComponent(statusError, planError);
  }

  return (
    <>
      {viewMode && viewMode != VIEW_MODE.FULLSCREEN ? (
        <WarehouseSearchZone />
      ) : (
        <></>
      )}
      <PlanDisplayer plan={activeWarehouse.plan} displayType={displayType} />
    </>
  );
}

function createDisplayerErrorComponent(statusError: FetchBaseQueryError | undefined, planError: FetchBaseQueryError | undefined) {
  if (statusError && (statusError as FetchBaseQueryError).data) {
    return createErrorComponent(statusError);
  } else {
    return createErrorComponent(planError!);
  }
}